import {
  CLIENT_GROUP_REFUSALS,
  ClientAdministrationRow,
  ClientGroup,
  ClientGroupRefusal,
  ClientGroupValue,
} from "@/data/models/ClientAdministrationRow";

/**
 * The Gruppe card's dialog (#2610074): the group it edits, the Mandanten it offers, and IRES Cloud's
 * refusals as messages. Pure.
 */

/** The group as the dialog edits it and IRES Cloud takes it. */
export function toClientGroupValue(group: ClientGroup): ClientGroupValue {
  return {
    isMaster: group.isMaster,
    memberClientIds: group.members.map((member) => member.id),
    settingsSourceClientId: group.settingsSource?.id ?? null,
  };
}

/** Whether two groups differ, the members in any order. */
export function clientGroupsDiffer(a: ClientGroupValue, b: ClientGroupValue): boolean {
  const sorted = (ids: string[]) => [...ids].sort().join(",");
  return a.isMaster !== b.isMaster ||
    sorted(a.memberClientIds) !== sorted(b.memberClientIds) ||
    a.settingsSourceClientId !== b.settingsSourceClientId;
}

/** One Mandant offered in the members picker. */
export interface GroupMemberChoice {
  value: string;
  text: string;
}

/**
 * The members picker's choices: the current members always (an inactive or deleted one marked), and every
 * Mandant that may join, as IRES Cloud decides it: not deleted, active, not a MASTER-Mandant, not this
 * client, in no other group. By name.
 *
 * @param rows - the Mandanten list.
 * @param clientId - the MASTER-Mandant being edited.
 * @param group - its group as the card loaded it.
 * @param inactiveSuffix - appended to an inactive member ("(inaktiv)").
 * @param deletedSuffix - appended to a deleted member ("(gelöscht)").
 */
export function groupMemberChoices(
  rows: ClientAdministrationRow[],
  clientId: string,
  group: ClientGroup,
  inactiveSuffix: string,
  deletedSuffix: string
): GroupMemberChoice[] {
  const memberIds = group.members.map((member) => member.id);
  const members = group.members.map((member) => ({
    value: member.id,
    text: member.deleted ? `${member.name} ${deletedSuffix}` : member.isActive ? member.name : `${member.name} ${inactiveSuffix}`,
  }));
  const candidates = rows
    .filter((row) =>
      !memberIds.includes(row.id) &&
      row.id !== clientId &&
      !row.deleted &&
      row.isActive !== false &&
      row.isMasterClient !== true &&
      // In no group, or already pointing at this master without being listed (as IRES Cloud allows).
      ((row.masterClientId ?? null) === null || row.masterClientId === clientId)
    )
    .map((row) => ({ value: row.id, text: row.clientName?.trim() || row.id }));

  return [...members, ...candidates].sort((a, b) => a.text.localeCompare(b.text, "de"));
}

/** The settings source picker: none, or one of the members chosen. */
export function settingsSourceChoices(
  memberChoices: GroupMemberChoice[],
  memberClientIds: string[],
  noneText: string
): Array<{ value: string | null; text: string }> {
  return [
    { value: null, text: noneText },
    ...memberChoices.filter((choice) => memberClientIds.includes(choice.value)),
  ];
}

/** IRES Cloud's refusal reason in a failed call, else null. */
export function clientGroupRefusalOf(error: unknown): ClientGroupRefusal | null {
  const reason = (error as { details?: { reason?: unknown } } | null)?.details?.reason;
  return typeof reason === "string" && CLIENT_GROUP_REFUSALS.includes(reason as ClientGroupRefusal)
    ? (reason as ClientGroupRefusal)
    : null;
}

/**
 * The message for a failed save, with the Mandanten it names: a duplicate Niederlassung number names its
 * number and both Mandanten, a Mandant that cannot join names them.
 *
 * @param error - the failed call.
 * @param nameOf - a Mandant's name by id.
 */
export function clientGroupError(error: unknown, nameOf: (clientId: string) => string): { key: string; params: string[] } {
  const reason = clientGroupRefusalOf(error);
  if (reason === null) {
    return { key: "admin.clients.group.saveFailed", params: [] };
  }
  const details = (error as { details?: { clientIds?: unknown; branchNumber?: unknown } }).details ?? {};
  const names = Array.isArray(details.clientIds) ? details.clientIds.map((id) => nameOf(String(id))) : [];
  if (reason === "DUPLICATE_BRANCH_NUMBERS") {
    return { key: `admin.clients.group.refusals.${reason}`, params: [String(details.branchNumber ?? ""), names[0] ?? "", names[1] ?? ""] };
  }
  if (reason === "NOT_A_CANDIDATE") {
    return { key: `admin.clients.group.refusals.${reason}`, params: [names.join(", ")] };
  }
  return { key: `admin.clients.group.refusals.${reason}`, params: [] };
}
